import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { PhysicsThread } from "./physics_thread.js";
import { RigidMotion, applyRigidMotion } from "./physics_protocol.js";
import { PhysicsStack } from "./physics_masses.js";

export class PhysicsWorld
{
  private static readonly BASE_GRAVITY = 9.81;

  private static readonly GRAVITY_SCALE = 1.2;
  public static readonly GRAVITY = PhysicsWorld.BASE_GRAVITY * PhysicsWorld.GRAVITY_SCALE;

  private static world: RAPIER.World;
  private static pre_step_hook: ( ( dt: number ) => void ) | null = null;

  public static async init()
  {
    await RAPIER.init();

    this.world = new RAPIER.World( {
      x: 0,
      y: -PhysicsWorld.GRAVITY,
      z: 0
    } );

    this.world.maxCcdSubsteps = PhysicsStack.ccd_substeps;

    console.log( "Rapier initialized" );

    if ( PhysicsWorld.threadRequested() )
      PhysicsThread.start( this.world, new URL( `./physics_worker.js?v=${typeof __CRASH_BUILD_ID__ === "string" ? __CRASH_BUILD_ID__ : "dev"}`, import.meta.url ) );
  }

  private static threadRequested(): boolean
  {
    if ( typeof Worker === "undefined" )
      return false;
    try { return localStorage.getItem( "bb_physics_thread" ) !== "0"; } catch { return true; }
  }

  private static readonly STARVED_SHARE = 0.5;
  private static readonly STARVED_WINDOWS = 3;
  private static readonly SUSPEND_MAX_WORKER_STEP_MS = 20;
  private static readonly RESUME_STEP_MS = 6;
  private static readonly RESUME_MIN_MS = 3000;
  private static readonly RESUME_SAMPLE_STEPS = 120;

  private static suspended = false;
  private static suspended_at = 0;
  private static suspensions = 0;
  private static window_wall = 0;
  private static window_starved = 0;
  private static window_step_ms = 0;
  private static window_steps = 0;
  private static starved_windows = 0;
  private static main_cost = 0;
  private static main_steps = 0;

  public static isThreaded(): boolean
  {
    return PhysicsThread.isActive() && !this.suspended;
  }

  public static threadState(): Record<string, unknown>
  {
    return { ...PhysicsThread.debugState(), suspended: this.suspended, suspensions: this.suspensions };
  }

  public static noteThreadedFrame( dt: number, starved: boolean ): void
  {
    this.window_wall += dt;
    if ( starved )
      this.window_starved += dt;
    if ( this.window_wall < 1 )
      return;

    const share = this.window_starved / this.window_wall;
    const worker_step = this.window_steps > 0 ? this.window_step_ms / this.window_steps : 0;
    this.window_wall = 0;
    this.window_starved = 0;
    this.window_step_ms = 0;
    this.window_steps = 0;
    const main_would_help = worker_step < PhysicsWorld.SUSPEND_MAX_WORKER_STEP_MS;
    this.starved_windows = share > PhysicsWorld.STARVED_SHARE && main_would_help ? this.starved_windows + 1 : 0;
    if ( this.starved_windows < PhysicsWorld.STARVED_WINDOWS )
      return;

    this.starved_windows = 0;
    this.suspended = true;
    this.suspensions++;
    this.suspended_at = performance.now();
    this.main_cost = 0;
    this.main_steps = 0;
    PhysicsThread.markDirty();
  }

  public static noteMainFrame( steps: number ): void
  {
    if ( !this.suspended || !PhysicsThread.isActive() )
      return;

    this.main_cost += this.step_ms;
    this.main_steps += steps;
    if ( performance.now() - this.suspended_at < PhysicsWorld.RESUME_MIN_MS || this.main_steps < PhysicsWorld.RESUME_SAMPLE_STEPS )
      return;

    const average = this.main_cost / this.main_steps;
    this.main_cost = 0;
    this.main_steps = 0;
    if ( average < PhysicsWorld.RESUME_STEP_MS )
    {
      this.suspended = false;
      this.window_wall = 0;
      this.window_starved = 0;
    }
  }

  public static canStepThreaded(): boolean
  {
    return PhysicsThread.canStep();
  }

  public static stepThreaded( dt: number, substeps = 1 ): void
  {
    PhysicsThread.beginStep();
    const t0 = performance.now();
    this.pre_step_hook?.( dt );
    PhysicsWorld.hook_ms += performance.now() - t0;
    PhysicsThread.sendStep( dt, substeps );
  }

  public static pumpThreaded(): boolean
  {
    const applied = PhysicsThread.pump();
    if ( applied )
    {
      PhysicsWorld.step_ms = PhysicsThread.step_ms;
      this.window_step_ms += PhysicsThread.step_ms;
      this.window_steps++;
    }
    return applied;
  }

  static createFixedBox(
    pos: THREE.Vector3,
    size: THREE.Vector3
  )
  {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed()
        .setTranslation( pos.x, pos.y, pos.z )
    );

    this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(
        size.x / 2,
        size.y / 2,
        size.z / 2
      ),
      body
    );

    return body;
  }

  public static moveBodies( bodies: readonly RAPIER.RigidBody[], motion: RigidMotion ): void
  {
    if ( bodies.length === 0 || PhysicsThread.moveBodies( this.world, bodies, motion ) )
      return;
    for ( const body of bodies )
      applyRigidMotion( body, motion );
  }

  public static setPreStepHook( hook: ( ( dt: number ) => void ) | null ): void
  {
    this.pre_step_hook = hook;
  }

  public static hook_ms = 0;
  public static step_ms = 0;

  public static resetTimers(): void
  {
    this.hook_ms = 0;
    this.step_ms = 0;
  }

  public static update( dt: number )
  {
    const t0 = performance.now();
    this.pre_step_hook?.( dt );

    const t1 = performance.now();
    if ( PhysicsThread.isActive() )
      PhysicsThread.markDirty();
    this.world.timestep = dt;
    this.world.step();

    const t2 = performance.now();
    PhysicsWorld.hook_ms += t1 - t0;
    PhysicsWorld.step_ms += t2 - t1;
  }

  public static counts(): { bodies: number; colliders: number; awake: number }
  {
    let awake = 0;
    this.world.forEachRigidBody( body =>
    {
      if ( !body.isSleeping() && body.isDynamic() )
        awake++;
    } );

    return {
      bodies: this.world.bodies.len(),
      colliders: this.world.colliders.len(),
      awake,
    };
  }

  // Reflects everything that moves across the plane x = axis_x: positions,
  // orientations and both velocities. Static and kinematic bodies are left to
  // whoever owns them.
  public static mirrorDynamicBodies( axis_x: number ): void
  {
    this.world.forEachRigidBody( body =>
    {
      if ( !body.isDynamic() )
        return;

      const wake = !body.isSleeping();
      const p = body.translation();
      const q = body.rotation();
      const v = body.linvel();
      const w = body.angvel();

      body.setTranslation( { x: 2 * axis_x - p.x, y: p.y, z: p.z }, wake );
      body.setRotation( { x: q.x, y: -q.y, z: -q.z, w: q.w }, wake );
      body.setLinvel( { x: -v.x, y: v.y, z: v.z }, wake );
      body.setAngvel( { x: w.x, y: -w.y, z: -w.z }, wake );
    } );
  }

  public static setFastMode( fast: boolean ): void
  {
    this.world.numSolverIterations = fast ? 2 : 4;
  }

  public static getWorld()
  {
    return this.world;
  }
}
